import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  ChapterCreateInputs,
  ChapterNameCheckInputs,
  ChapterRemapInputs,
  ChapterTopicMappingInput,
  ChapterUpdateInputs,
  ClassCreateInputs,
  ClassUpdateInputs,
  ConceptCreateInputs,
  ConceptNameCheckInputs,
  ConceptNodeCreateInputs,
  ConceptRemapInputs,
  ConceptUpdateInputs,
  ContentCleanNameDisplay,
  CourseCreateInputs,
  CourseSubjectMappingInput,
  CourseUpdateInputs,
  ModuleCreateInputs,
  ModuleUpdateInputs,
  SubjectChapterMappingInput,
  SubjectCreateInputs,
  SubjectNameCheckInputs,
  SubjectRemapInputs,
  SubjectUpdateInputs,
  TopicConceptMappingInput,
  TopicCreateInputs,
  TopicNameCheckInputs,
  TopicRemapInputs,
  TopicUpdateInputs,
} from "@/types/content";
import {
  ChapterContentDesignUpdate,
  ChapterDesignCreate,
  ConceptContentUpdate,
  ConceptDesignCreate,
  ContentDesignLink,
  CourseContentUpdate,
  CourseDesignCreate,
  QuestionContentUpdate,
  SubjectContentUpdate,
  SubjectDesignCreate,
  TopicContentUpdate,
  TopicDesignCreate,
} from "@/types/contentDesign";
import { ContentDesignIdResponse } from "@/types/contentV2Versioning";
import type { ApiResponse, AzureUploadUrl } from "@/types/global";

export default class ContentBaseApi extends ApiVerified {
  async saveCourse(inputs: CourseCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/courses`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveClass(inputs: ClassCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/classes`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveModule(inputs: ModuleCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/modules`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveSubject(inputs: SubjectCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/subjects`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveChapter(inputs: ChapterCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/chapters`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTopic(inputs: TopicCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/topics`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveConcept(inputs: ConceptNodeCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<null>(`content-base/concepts`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptDiagramUploadUrl(contentType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `content-base/concept-diagram-upload-url`,
        { params: { contentType: contentType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Entity update (PUT content-base/{type}/{id}) — edit the SQL row's Name/DisplayRank/
  // IsActive in place. One explicit endpoint per level; all return boolean.
  async updateCourse(courseId: number, inputs: CourseUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/courses/${courseId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateClass(classId: number, inputs: ClassUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/classes/${classId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateModule(moduleId: number, inputs: ModuleUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/modules/${moduleId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSubject(subjectId: number, inputs: SubjectUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/subjects/${subjectId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateChapter(chapterId: number, inputs: ChapterUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/chapters/${chapterId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTopic(topicId: number, inputs: TopicUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/topics/${topicId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateConcept(conceptId: number, inputs: ConceptUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/concepts/${conceptId}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Content name check (POST content-base/{type}/get-name) — read-only dry-run that returns
  // ContentCleanNameDisplay { CleanName, UseExisting, SuggestedName, Status } to decide
  // reuse-existing / rename-in-place / create-new. Junction ids are optional (omit for add-new).
  async getSubjectName(inputs: SubjectNameCheckInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentCleanNameDisplay>(
        `content-base/subjects/get-name`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getChapterName(inputs: ChapterNameCheckInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentCleanNameDisplay>(
        `content-base/chapters/get-name`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTopicName(inputs: TopicNameCheckInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentCleanNameDisplay>(
        `content-base/topics/get-name`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptName(inputs: ConceptNameCheckInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentCleanNameDisplay>(
        `content-base/concepts/get-name`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Re-map (PUT content-base/{junction}/remap) — re-point the selected junction rows to an
  // existing entity (the id chosen from a get-name UseExisting / SuggestedName result).
  async remapCourseSubjects(inputs: SubjectRemapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/course-subjects/remap`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async remapSubjectChapters(inputs: ChapterRemapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/subject-chapters/remap`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async remapChapterTopics(inputs: TopicRemapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/chapter-topics/remap`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async remapTopicConcepts(inputs: ConceptRemapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/topic-concepts/remap`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Hierarchy mapping saves (PUT) — flat lists keyed by junction ids. IsNewEntry rows insert;
  // the rest update DisplayRank + IsActive. Both return boolean.
  async saveCourseSubjectMapping(
    courseId: number,
    subjectMappings: Array<CourseSubjectMappingInput>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/courses/${courseId}/subjects`,
        subjectMappings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveSubjectChapterMapping(
    courseSubjectId: number,
    chapterMappings: Array<SubjectChapterMappingInput>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/course-subjects/${courseSubjectId}/chapters`,
        chapterMappings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat list of topics mapped under one chapter (junction SubjectChapterId).
  async saveChapterTopicMapping(
    subjectChapterId: number,
    topicMappings: Array<ChapterTopicMappingInput>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/subject-chapters/${subjectChapterId}/topics`,
        topicMappings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat list of concepts mapped under one topic (junction ChapterTopicId). The endpoint keys the
  // topic in the body, not the path.
  async saveTopicConceptMapping(
    chapterTopicId: number,
    conceptMappings: Array<TopicConceptMappingInput>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/topic-concept-mappings`,
        { ChapterTopicId: chapterTopicId, ConceptMappings: conceptMappings },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Design create (Step 1: write the Mongo design doc) — one explicit endpoint per level.
  // All six create endpoints return the uniform ContentDesignIdResponse ({ DesignId }).
  async createCourseDesign(body: CourseDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/course-designs`,
        body,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createSubjectDesign(body: SubjectDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/subject-designs`,
        body,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createChapterDesign(body: ChapterDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/chapter-designs`,
        body,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createTopicDesign(body: TopicDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/topic-designs`,
        body,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createConceptDesign(body: ConceptDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/concept-designs`,
        body,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Design link (Step 2: bind a design ObjectId to its SQL row) — one endpoint per level.
  async linkCourseDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/course-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkSubjectDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/subject-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkChapterDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/chapter-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkTopicDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/topic-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkConceptDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/concept-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkQuestionDesign(link: ContentDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-base/question-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Design update (Step 3: edit the Mongo design doc in place) — designId comes from the body.
  async updateCourseDesign(body: CourseContentUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's CourseDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { CourseDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/course-designs/${CourseDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSubjectDesign(body: SubjectContentUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's SubjectDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { SubjectDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/subject-designs/${SubjectDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateChapterDesign(body: ChapterContentDesignUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's ChapterDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { ChapterDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/chapter-designs/${ChapterDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTopicDesign(body: TopicContentUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's TopicDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { TopicDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/topic-designs/${TopicDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateConceptDesign(body: ConceptContentUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's ConceptDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { ConceptDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/concept-designs/${ConceptDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateQuestionDesign(body: QuestionContentUpdate): Promise<ApiResponse> {
    try {
      // The design id addresses the resource in the path; content-base.json's QuestionDesignUpdate
      // schema has no such property, so it is stripped from the body rather than sent twice.
      const { QuestionDesignId, ...wireBody } = body;
      const response = await this.axiosInstance.put<boolean>(
        `content-base/question-designs/${QuestionDesignId}`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Design fork (POST entity-collection/{entityId}/design-fork, empty body) — Course/Subject/
  // Chapter/Topic/Concept only. Returns ContentDesignIdResponse ({ DesignId }).
  async forkCourseDesign(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/courses/${courseId}/design-fork`,
        {},
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkSubjectDesign(courseSubjectId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/course-subjects/${courseSubjectId}/design-fork`,
        {},
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkChapterDesign(subjectChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/subject-chapters/${subjectChapterId}/design-fork`,
        {},
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkTopicDesign(chapterTopicId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/chapter-topics/${chapterTopicId}/design-fork`,
        {},
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkConceptDesign(topicConceptId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentDesignIdResponse>(
        `content-base/topic-concepts/${topicConceptId}/design-fork`,
        {},
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── Concept create-and-map: create a concept SQL row under a topic (returns the new ConceptId).
  async saveNewConceptInTopic(
    chapterTopicId: number,
    concept: ConceptCreateInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `content-base/chapter-topics/${chapterTopicId}/concepts`,
        concept,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
